import { getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasBackendApi } from './atlasBackendClient';
import type { MetricUnknownReason, ProfileMetric, ProfileStats } from '../../types/profile';
import { createUnknownProfileStats, knownMetric, unknownMetric } from '../../types/profile';

type MetricInput =
  | number
  | null
  | undefined
  | {
      status?: string;
      value?: number | null;
      reason?: string;
      known?: boolean;
    };

type RawProfileStats = Record<string, unknown> & {
  stats?: Record<string, unknown>;
};

type FetchProfileStatsParams = {
  userId: string;
  effectiveTimezone: string;
  signal?: AbortSignal;
};

const API_BASE_URL = getSimulationApiBaseUrl();
const PROFILE_STATS_ENDPOINT = '/data_generator/user/profile_stats';
const USER_MAPS_ENDPOINT = '/data_generator/user/maps';

function getAuthToken(): string | null {
  const tokenString = localStorage.getItem('atlas_authToken');
  if (!tokenString) {
    return null;
  }

  try {
    const tokenObject = JSON.parse(tokenString);
    if (typeof tokenObject?.access_token === 'string' && tokenObject.access_token.trim()) {
      return tokenObject.access_token.trim();
    }
  } catch {
    // Token is stored as a raw string in most environments.
  }

  return tokenString.trim() || null;
}

function parseUnknownReason(input?: string): MetricUnknownReason {
  if (input === 'unsupported' || input === 'delayed' || input === 'error') {
    return input;
  }
  return 'unavailable';
}

function toMetric(input: MetricInput): ProfileMetric {
  if (typeof input === 'number' && Number.isFinite(input)) {
    return knownMetric(input);
  }

  if (!input || typeof input !== 'object') {
    return unknownMetric();
  }

  if (input.known === false || input.status === 'unknown') {
    return unknownMetric(parseUnknownReason(input.reason));
  }

  if ((input.status === 'known' || input.known === true) && typeof input.value === 'number') {
    return knownMetric(input.value);
  }

  if (typeof input.value === 'number') {
    return knownMetric(input.value);
  }

  return unknownMetric(parseUnknownReason(input.reason));
}

function fromShape(input: RawProfileStats): Record<string, unknown> {
  if (input.stats && typeof input.stats === 'object') {
    return input.stats as Record<string, unknown>;
  }
  return input;
}

function pickMetric(raw: Record<string, unknown>, keys: string[]): ProfileMetric {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(raw, key)) {
      return toMetric(raw[key] as MetricInput);
    }
  }
  return unknownMetric();
}

export function normalizeProfileStats(payload: unknown): ProfileStats {
  const base = createUnknownProfileStats();
  if (!payload || typeof payload !== 'object') {
    return base;
  }

  const raw = fromShape(payload as RawProfileStats);

  return {
    totalSimulationsRun: pickMetric(raw, ['totalSimulationsRun', 'total_simulations_run', 'simulationRuns', 'simulation_runs']),
    mapsSaved: pickMetric(raw, ['mapsSaved', 'maps_saved']),
    loginCount: pickMetric(raw, ['loginCount', 'login_count']),
    layersCreated: pickMetric(raw, ['layersCreated', 'layers_created']),
    routesGenerated: pickMetric(raw, ['routesGenerated', 'routes_generated']),
    activeDays: pickMetric(raw, ['activeDays', 'active_days']),
  };
}

async function fetchMapsSavedFallback(token: string, signal?: AbortSignal): Promise<ProfileMetric> {
  const response = await atlasBackendApi.get<{ total?: number }>(`${API_BASE_URL}${USER_MAPS_ENDPOINT}`, {
    params: { offset: 0, limit: 1 },
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (typeof response.data?.total === 'number') {
    return knownMetric(response.data.total);
  }

  return unknownMetric('unavailable');
}

export async function fetchProfileStats({ userId, effectiveTimezone, signal }: FetchProfileStatsParams): Promise<ProfileStats> {
  const token = getAuthToken();
  if (!token) {
    return createUnknownProfileStats();
  }

  let stats = createUnknownProfileStats();

  try {
    const response = await atlasBackendApi.get(`${API_BASE_URL}${PROFILE_STATS_ENDPOINT}`, {
      params: { userId, timezone: effectiveTimezone },
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    stats = normalizeProfileStats(response.data);
  } catch {
    // Keep partial fallback behavior below.
  }

  if (stats.mapsSaved.status === 'unknown') {
    try {
      stats = { ...stats, mapsSaved: await fetchMapsSavedFallback(token, signal) };
    } catch {
      // Preserve unknown state on fallback failures.
    }
  }

  return stats;
}
